import type {
  AxiosProgressEvent,
  AxiosRequestConfig,
  AxiosResponse,
  GenericAbortSignal,
} from 'axios'
import { baseAppService, baseAppServiceAuth } from './axios'
import { useAuthStore } from '@/store'

type HTTPMethod =
| 'get'
| 'post'
| 'put'
| 'patch'
| 'delete'
| 'request'
| 'head'
| 'options'

export interface HttpOption {
  url: string
  data?: any
  method?: any
  headers?: Record<string, any>
  onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void
  signal?: GenericAbortSignal
  beforeRequest?: () => void
  afterRequest?: () => void
  adapter?: any
  responseType?: AxiosRequestConfig['responseType']
  timeout?: number
}

export interface Response<T = any> {
  data: T
  message: string | null
  status: number
}

const handleSuccess = <T>(res: AxiosResponse<T>) => {
  const authStore = useAuthStore()

  if (res.status === 200 || res.status === 201)
    return res.data

  if (res.status === 401)
    authStore.removeToken()

  return Promise.reject(res)
}

const handleError = (error: any) => {
  const authStore = useAuthStore()
  if (error?.response?.status === 401)
    authStore.logout()

  return Promise.reject(error?.response?.data?.message ?? 'Error')
}

async function http<T = any>(options: HttpOption): Promise<T> {
  const {
    url,
    data,
    method = 'get',
    headers,
    onDownloadProgress,
    signal,
    beforeRequest,
    afterRequest,
    adapter,
    responseType,
    timeout,
  } = options

  beforeRequest?.()

  try {
    const params = typeof data === 'function' ? data() : data ?? {}

    let response: AxiosResponse<T>

    switch (method.toLowerCase()) {
      case 'get':
      case 'head':
      case 'options':
        response = await baseAppService.request({
          url,
          method,
          params,
          headers,
          signal,
          onDownloadProgress,
          adapter,
          responseType,
          timeout,
        })
        break

      case 'delete':
        // DELETE can have body in axios >= 0.19
        response = await baseAppService.delete(url, {
          data: params,
          headers,
          signal,
          onDownloadProgress,
          adapter,
          responseType,
          timeout,
        })
        break

      case 'post':
      case 'put':
      case 'patch':
        response = await baseAppService.request({
          url,
          method,
          data: params,
          headers,
          signal,
          onDownloadProgress,
          adapter,
          responseType,
          timeout,
        })
        break

      case 'request':
        response = await baseAppService.request({
          url,
          method,
          params,
          headers,
          signal,
          onDownloadProgress,
          adapter,
          responseType,
          timeout,
        })
        break

      default:
        response = await baseAppService.get(url, {
          params,
          headers,
          signal,
          onDownloadProgress,
          adapter,
          responseType,
          timeout,
        })
        break
    }

    afterRequest?.()
    return handleSuccess(response)
  }
  catch (error) {
    afterRequest?.()
    return handleError(error)
  }
}

export function get<T = any>(options: HttpOption): Promise<T> {
  return http<T>({ ...options, method: 'get' })
}

export function post<T = any>(options: HttpOption): Promise<T> {
  return http<T>({ ...options, method: 'post' })
}

export function put<T = any>(options: HttpOption): Promise<T> {
  return http<T>({ ...options, method: 'put' })
}

export function del<T = any>(options: HttpOption): Promise<T> {
  return http<T>({ ...options, method: 'delete' })
}

export function executeApp<T = any>(
  url: string,
  data?: any,
  method: HTTPMethod = 'get',
  configs: AxiosRequestConfig = {},
): Promise<T> {
  const lowerMethod = method.toLowerCase()

  if (['get', 'request', 'delete', 'head', 'options'].includes(lowerMethod)) {
    return baseAppService.request({
      url,
      method,
      params: data,
      ...configs,
    })
  }

  return baseAppService.request({
    url,
    method,
    data,
    ...configs,
  })
}

export function executeAppAuth<T = any>(
  url: string,
  data?: any,
  method: HTTPMethod = 'get',
  configs: AxiosRequestConfig = {},
): Promise<T> {
  const lowerMethod = method.toLowerCase()

  if (['get', 'request', 'delete', 'head', 'options'].includes(lowerMethod)) {
    return baseAppServiceAuth.request({
      url,
      method,
      params: data,
      ...configs,
    })
  }

  return baseAppServiceAuth.request({
    url,
    method,
    data,
    ...configs,
  })
}
